import { useState, useEffect, useContext } from 'react';
import { toast } from 'sonner';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { AuthContext } from '../../contexts/AuthContext';
import SEO from '../SEO';
import RulesTab from './tournament/RulesTab';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean'],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ script: 'sub' }, { script: 'super' }],
    ['blockquote', 'code-block'],
  ],
};

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'list',
  'bullet',
  'align',
  'link',
  'indent',
  'script',
  'blockquote',
  'code-block',
];

const Rules = () => {
  const [rules, setRules] = useState('');
  const [editedRules, setEditedRules] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { currentUser, isSuperAdmin } = useContext(AuthContext);

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const rulesDoc = await getDoc(doc(db, 'settings', 'rules'));
      if (rulesDoc.exists()) {
        setRules(rulesDoc.data().content);
        setEditedRules(rulesDoc.data().content);
      }
    } catch (error) {
      console.error('Error fetching rules:', error);
      toast.error('Failed to load rules.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEditing = () => {
    setIsEditing(true);
    setEditedRules(rules);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setEditedRules(rules);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await setDoc(doc(db, 'settings', 'rules'), {
        content: editedRules,
        lastUpdated: new Date(),
        updatedBy: currentUser?.email,
      });
      setRules(editedRules);
      setIsEditing(false);
      toast.success('Rules saved successfully.');
    } catch (error) {
      console.error('Error saving rules:', error);
      toast.error('Failed to save rules. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-screen-md px-4 py-8">
        <div className="flex min-h-[400px] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-screen-md px-4 py-8">
      <SEO
        title="Tournament Rules"
        description="Official KZCOMP tournament rules — eligibility, conduct, formats, scoring and dispute resolution for competitive KZ events."
        canonicalPath="/rules"
      />
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="font-display text-2xl">Tournament Rules</CardTitle>

          {isSuperAdmin && (
            <div className="flex gap-2">
              {!isEditing ? (
                <Button onClick={handleStartEditing}>Edit Rules</Button>
              ) : (
                <>
                  <Button variant="outline" onClick={handleCancelEditing}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </>
              )}
            </div>
          )}
        </CardHeader>

        <CardContent>
          {isEditing ? (
            <div className="[&_.ql-container]:font-sans [&_.ql-container]:text-base [&_.ql-editor]:min-h-[400px]">
              <ReactQuill
                value={editedRules}
                onChange={setEditedRules}
                modules={modules}
                formats={formats}
                theme="snow"
              />
            </div>
          ) : (
            <RulesTab html={rules} />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Rules;
